/**
 * Sale Order PDF Generation Service
 */
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { toast } from 'react-toastify';

/**
 * Generate HTML for Sale Order PDF
 * @param {Object} saleOrder - Sale order data
 * @param {Object} storeInfo - Company/store information
 * @param {string} language - Language code (en, fr, nl)
 * @returns {string} HTML string for PDF generation
 */
export const generateSaleOrderPdfHtml = (saleOrder, storeInfo, language = 'en') => {
  const {
    SaleOrderNumber,
    SaleOrderDate,
    Customer,
    LineItems = [],
    CustomText = '',
    VATPercentage = 21,
    Status = 'Draft',
    PaymentStatus = 'Not Paid',
    PaidAmount = 0,
    DiscountType = 'percentage',
    DiscountValue = 0,
    IsTvaIncluded = true,
  } = saleOrder;

  // Calculate totals
  const totalQuantity = LineItems.reduce((sum, item) => sum + (Number(item.Quantity) || 0), 0);
  const totalExcl = LineItems.reduce((sum, item) => sum + Number(item.TotalExcl || 0), 0);
  const totalIncl = LineItems.reduce((sum, item) => sum + Number(item.TotalIncl || 0), 0);
  
  // Calculate discount and final totals based on price type
  let discountAmount = 0;
  let subtotalAfterDiscount = 0;
  let vatAmount = 0;
  let finalTotal = 0;
  
  if (IsTvaIncluded) {
    // Prices include VAT
    if (DiscountValue > 0) {
      discountAmount = DiscountType === 'percentage' ? totalIncl * (DiscountValue / 100) : DiscountValue;
    }
    subtotalAfterDiscount = totalIncl - discountAmount;
    finalTotal = subtotalAfterDiscount;
    vatAmount = VATPercentage > 0 ? (subtotalAfterDiscount / (1 + VATPercentage / 100)) * (VATPercentage / 100) : 0;
  } else {
    // Prices exclude VAT
    if (DiscountValue > 0) {
      discountAmount = DiscountType === 'percentage' ? totalExcl * (DiscountValue / 100) : DiscountValue;
    }
    subtotalAfterDiscount = totalExcl - discountAmount;
    vatAmount = VATPercentage > 0 ? subtotalAfterDiscount * (VATPercentage / 100) : 0;
    finalTotal = subtotalAfterDiscount + vatAmount;
  }
  
  const dueAmount = finalTotal - PaidAmount;
  
  const lineItemsHtml = LineItems.map((item, index) => {
    const quantity = Number(item.Quantity) || 0;
    const unitPrice = Number(item.UnitPrice) || 0;
    const totalExcl = Number(item.TotalExcl) || (quantity * unitPrice);
    const totalIncl = Number(item.TotalIncl) || (totalExcl * (1 + VATPercentage / 100));
    const rowClass = index % 2 === 1 ? 'alternate-row' : '';

    return `
      <tr class="${rowClass}">
        <td style="padding: 6px 8px; border-bottom: 1px solid #ddd; font-size: 8pt;">${index + 1}</td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #ddd; text-align: center; font-size: 8pt;">${item.SKU || item.BarCode || '-'}</td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #ddd; font-size: 8pt;">
          ${item.Name || item.ProductName || 'Unknown Product'}
          ${item.Description ? `<br/><span style="color: #555; font-size: 7pt;">${item.Description}</span>` : ''}
        </td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #ddd; text-align: center; font-size: 8pt;">${quantity}</td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #ddd; text-align: right; font-size: 8pt;">€${unitPrice.toFixed(2)}</td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #ddd; text-align: right; font-size: 8pt;">€${totalIncl.toFixed(2)}</td>
      </tr>
    `;
  }).join('');

  // Format date
  const formatDate = (date) => {
    if (!date) return new Date().toLocaleDateString(language);
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Get labels based on language
  const labels = getLabels(language);

  // Format customer address without country
  const customerAddress = [
    Customer?.Street || '',
    Customer?.City && Customer?.PostCode ? `${Customer.PostCode} ${Customer.City}` : (Customer?.City || Customer?.PostCode || '')
  ].filter(Boolean).join(', ');

  // Format store address without country
  const storeAddress = [
    storeInfo?.Address || storeInfo?.address || '',
    storeInfo?.City && storeInfo?.PostCode ? `${storeInfo.PostCode} ${storeInfo.City}` : 
    (storeInfo?.city && storeInfo?.postCode ? `${storeInfo.postCode} ${storeInfo.city}` : 
    (storeInfo?.City || storeInfo?.city || storeInfo?.PostCode || storeInfo?.postCode || ''))
  ].filter(Boolean).join(', ');

  const html = `
<!DOCTYPE html>
<html lang="${language}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${labels.saleOrder} - ${SaleOrderNumber}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { 
      font-family: 'Arial', 'Helvetica', sans-serif; 
      font-size: 8pt; 
      color: #000; 
      line-height: 1.3;
      background: white;
    }
    .page { 
      width: 794px; 
      min-height: 1123px; 
      padding: 30px; 
      margin: 0 auto; 
      background: white; 
      position: relative; 
    }
    .watermark {
      position: absolute;
      top: 30px;
      left: 30px;
      font-size: 32pt;
      font-weight: bold;
      color: rgba(0, 0, 0, 0.08);
      z-index: 0;
    }
    .header { 
      position: relative;
      z-index: 1;
      margin-bottom: 40px;
    }
    .store-info { 
      margin-top: 45px;
      margin-bottom: 15px;
    }
    .store-name { 
      font-size: 11pt; 
      font-weight: bold; 
      color: #000; 
      margin-bottom: 4px; 
    }
    .store-details { 
      font-size: 8pt; 
      color: #000; 
      line-height: 1.4; 
    }
    .order-info { 
      position: absolute;
      top: 30px;
      right: 30px;
      text-align: right;
      max-width: 45%;
    }
    .order-date {
      font-size: 8pt;
      color: #000;
      margin-bottom: 4px;
    }
    .order-number { 
      font-size: 10pt; 
      font-weight: bold; 
      color: #000; 
      margin-bottom: 8px;
    }
    .order-customer { 
      font-size: 8pt; 
      color: #000; 
      line-height: 1.4;
      margin-top: 8px;
    }
    .customer-info { 
      margin-top: 15px;
      margin-bottom: 20px;
    }
    .customer-label { 
      font-size: 9pt; 
      font-weight: 600; 
      color: #000; 
      margin-bottom: 4px; 
    }
    .customer-details { 
      font-size: 8pt; 
      color: #000; 
      line-height: 1.4; 
    }
    table { 
      width: 100%; 
      border-collapse: collapse; 
      margin-bottom: 20px;
      page-break-inside: auto;
    }
    tr {
      page-break-inside: avoid;
      page-break-after: auto;
    }
    thead {
      display: table-header-group;
    }
    th { 
      background: #fff; 
      color: #000; 
      padding: 8px 8px; 
      text-align: left; 
      font-weight: 600; 
      font-size: 8pt;
      border-bottom: 2px solid #000;
      border-top: 2px solid #000;
    }
    th:nth-child(4), th:nth-child(5), th:nth-child(6) { 
      text-align: right; 
    }
    th:nth-child(2) {
      text-align: center;
    }
    th:nth-child(4) {
      text-align: center;
    }
    .alternate-row {
      background-color: #f5f5f5;
    }
    td { 
      padding: 6px 8px; 
      border-bottom: 1px solid #ddd; 
      font-size: 8pt;
    }
    .totals { 
      margin-top: 15px; 
      margin-left: auto; 
      width: 45%; 
      border-top: 2px solid #000;
      padding-top: 8px;
    }
    .totals-row { 
      display: flex; 
      justify-content: space-between; 
      padding: 4px 0; 
      font-size: 8pt;
    }
    .totals-row.grand-total { 
      font-size: 9pt; 
      font-weight: bold; 
      color: #000; 
      border-top: 2px solid #000; 
      margin-top: 6px; 
      padding-top: 8px; 
    }
    .custom-text { 
      margin-top: 20px; 
      padding: 12px; 
      border: 1px solid #333;
      border-radius: 4px;
      page-break-inside: avoid;
    }
    .custom-text-label { 
      font-weight: 600; 
      color: #000; 
      margin-bottom: 6px;
      font-size: 8pt;
    }
    .footer { 
      position: absolute;
      bottom: 30px;
      left: 30px;
      right: 30px;
      padding-top: 12px; 
      border-top: 1px solid #333; 
      text-align: center; 
      font-size: 9pt; 
      color: #000;
      page-break-inside: avoid;
    }
    @media print {
      .page { 
        margin: 0; 
        box-shadow: none; 
        page-break-after: always;
      }
      @page { 
        size: A4; 
        margin: 0; 
      }
      tr {
        page-break-inside: avoid !important;
      }
    }
  </style>
</head>
<body>
  <div class="page">
    <!-- Watermark -->
    <div class="watermark">${labels.saleOrder}</div>
    
    <!-- Header -->
    <div class="header">
      <!-- Store Info (Left - under SALE ORDER text) -->
      <div class="store-info">
        <div class="store-name">${storeInfo?.StoreName || storeInfo?.CompanyName || storeInfo?.storeName || storeInfo?.name || 'Company Name'}</div>
        <div class="store-details">
          ${storeAddress}<br/>
          ${storeInfo?.VATNumber || storeInfo?.vat ? `${labels.vat}: ${storeInfo.VATNumber || storeInfo.vat}` : ''}
        </div>
      </div>
      
      <!-- Order Info Top Right (Date first, then Sale Order Number, then Customer) -->
      <div class="order-info">
        <div class="order-date">${labels.date}: ${formatDate(SaleOrderDate)}</div>
        <div class="order-number">${SaleOrderNumber || 'N/A'}</div>
        <div class="order-customer">
          <strong>${Customer?.CompanyName || Customer?.Name || 'Unknown Customer'}</strong><br/>
          ${customerAddress}<br/>
          ${Customer?.VATNumber ? `${labels.vat}: ${Customer.VATNumber}` : ''}
        </div>
      </div>
      
      <!-- Customer Info (Hidden - content moved to order-info) -->
      <div class="customer-info" style="display: none;">
        <div class="customer-label">${labels.customer}:</div>
        <div class="customer-details">
          <strong>${Customer?.CompanyName || Customer?.Name || 'Customer'}</strong><br/>
          ${customerAddress}<br/>
          ${Customer?.VATNumber ? `${labels.vat}: ${Customer.VATNumber}` : ''}
        </div>
      </div>
    </div>

    <!-- Line Items Table -->
    <table>
      <thead>
        <tr>
          <th style="width: 5%;">#</th>
          <th style="width: 12%; text-align: center;">${labels.sku}</th>
          <th style="width: 38%;">${labels.description}</th>
          <th style="width: 12%; text-align: center;">${labels.quantity}</th>
          <th style="width: 16.5%; text-align: right;">${labels.unitPrice}</th>
          <th style="width: 16.5%; text-align: right;">${labels.total}</th>
        </tr>
      </thead>
      <tbody>
        ${lineItemsHtml}
        <tr style="background-color: #e0e0e0; font-weight: 600;">
          <td style="padding: 8px; border-bottom: 2px solid #000; font-size: 8pt;" colspan="2"></td>
          <td style="padding: 8px; border-bottom: 2px solid #000; font-size: 8pt;">${labels.total}</td>
          <td style="padding: 8px; border-bottom: 2px solid #000; text-align: center; font-size: 8pt;">${totalQuantity}</td>
          <td style="padding: 8px; border-bottom: 2px solid #000; text-align: right; font-size: 8pt;"></td>
          <td style="padding: 8px; border-bottom: 2px solid #000; text-align: right; font-size: 8pt;">€${(IsTvaIncluded ? totalIncl : finalTotal).toFixed(2)}</td>
        </tr>
      </tbody>
    </table>

    <!-- Totals -->
    <div class="totals">
      <div class="totals-row">
        <span>${labels.subtotal}:</span>
        <span>€${(IsTvaIncluded ? totalIncl : totalExcl).toFixed(2)}</span>
      </div>
      ${DiscountValue > 0 ? `
        <div class="totals-row" style="color: #ff9800; font-weight: 600;">
          <span>${labels.discount} ${DiscountType === 'percentage' ? `(${DiscountValue}%)` : ''}:</span>
          <span>-€${discountAmount.toFixed(2)}</span>
        </div>
        <div class="totals-row" style="font-weight: 600;">
          <span>${labels.subtotalAfterDiscount}:</span>
          <span>€${subtotalAfterDiscount.toFixed(2)}</span>
        </div>
      ` : ''}
      ${!IsTvaIncluded && VATPercentage > 0 ? `
        <div class="totals-row">
          <span>${labels.vat} (${VATPercentage}%):</span>
          <span>€${vatAmount.toFixed(2)}</span>
        </div>
      ` : ''}
      <div class="totals-row grand-total">
        <span>${labels.grandTotal}:</span>
        <span>€${finalTotal.toFixed(2)}</span>
      </div>
      ${PaidAmount > 0 ? `
        <div class="totals-row">
          <span>${labels.paidAmount}:</span>
          <span>€${Number(PaidAmount).toFixed(2)}</span>
        </div>
        <div class="totals-row" style="font-weight: 600;">
          <span>${labels.dueAmount}:</span>
          <span>€${dueAmount.toFixed(2)}</span>
        </div>
      ` : ''}
    </div>

    <!-- Custom Text/Notes -->
    ${CustomText ? `
      <div class="custom-text">
        <div class="custom-text-label">${labels.notes}:</div>
        <div>${CustomText}</div>
      </div>
    ` : ''}

    <!-- Footer -->
    <div class="footer">
      <div style="font-weight: 600;">${labels.thankYou}</div>
    </div>
  </div>
</body>
</html>
  `;

  return html;
};

/**
 * Generate and preview PDF using html2canvas and jsPDF (same as invoice)
 */
export const generateAndPreviewSaleOrderPDF = async (saleOrder, storeInfo, language = 'en', t = (key) => key) => {
  try {
    // Check if store info is loaded
    if (!storeInfo) {
      console.error('Store info not available:', { storeInfo });
      toast.error(t('store_info_not_loaded') || 'Store information not loaded. Please refresh the page.');
      return false;
    }

    // Generate HTML
    const filledTemplate = generateSaleOrderPdfHtml(saleOrder, storeInfo, language);

    // Create temporary div
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = filledTemplate;
    
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.width = '794px'; // A4 width in pixels at 96 DPI
    tempDiv.style.background = 'white';
    document.body.appendChild(tempDiv);

    // Wait for images to load (if any)
    const images = tempDiv.getElementsByTagName('img');
    const imagePromises = Array.from(images).map(img => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        const timeout = setTimeout(() => resolve(), 3000);
        img.onload = () => {
          clearTimeout(timeout);
          resolve();
        };
        img.onerror = () => {
          clearTimeout(timeout);
          resolve();
        };
      });
    });

    await Promise.all(imagePromises);

    // Wait a bit for rendering
    await new Promise(resolve => setTimeout(resolve, 100));

    // Generate canvas
    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      logging: false,
      width: 794,
      windowWidth: 794,
      backgroundColor: '#ffffff',
      onclone: (clonedDoc) => {
        clonedDoc.body.style.background = 'white';
      }
    });

    document.body.removeChild(tempDiv);

    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      throw new Error('Invalid canvas generated');
    }

    // Create PDF
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    
    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;

    // Handle multi-page if needed
    let heightLeft = imgHeight;
    let position = 0;
    let pageCount = 1;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;

    while (heightLeft > 10) { // Changed from 0 to 10 to prevent extra empty pages
      position = heightLeft - imgHeight;
      pdf.addPage();
      pageCount++;
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
    }
    
    // Add page numbers to all pages
    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setTextColor(85, 85, 85);
      pdf.text(`${i}/${totalPages}`, pdfWidth - 15, pdfHeight - 10, { align: 'right' });
    }

    // Open in new tab
    const pdfBlob = pdf.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
    
    return true;
  } catch (error) {
    console.error('❌ Sale Order PDF generation error:', error);
    
    if (error.message.includes('Invalid canvas')) {
      toast.error(t('failed_to_render_sale_order') || 'Failed to render sale order');
    } else if (error.message.includes('html2canvas')) {
      toast.error(t('failed_to_convert_html') || 'Failed to convert HTML');
    } else {
      toast.error(t('failed_to_generate_pdf') || 'Failed to generate PDF. Please try again.');
    }
    return false;
  }
};

/**
 * Get labels for different languages
 */
function getLabels(language) {
  const translations = {
    en: {
      saleOrder: 'Sale Order',
      saleOrderNumber: 'Sale Order #',
      date: 'Date',
      customer: 'Customer Details',
      sku: 'SKU/Bar Code',
      description: 'Description',
      quantity: 'Quantity',
      unitPrice: 'Unit Price',
      total: 'Total',
      totalQuantity: 'Total Quantity',
      subtotal: 'Subtotal',
      subtotalAfterDiscount: 'Subtotal After Discount',
      vat: 'VAT',
      discount: 'Discount',
      grandTotal: 'Grand Total',
      paidAmount: 'Paid Amount',
      dueAmount: 'Due Amount',
      notes: 'Notes',
      phone: 'Phone',
      email: 'Email',
      status: 'Status',
      paymentStatus: 'Payment Status',
      thankYou: 'Thank you for your business!',
    },
    fr: {
      saleOrder: 'Bon de Commande',
      saleOrderNumber: 'N° Commande',
      date: 'Date',
      customer: 'Détails Client',
      sku: 'SKU/Code-barres',
      description: 'Description',
      quantity: 'Quantité',
      unitPrice: 'Prix Unitaire',
      total: 'Total',
      totalQuantity: 'Quantité Totale',
      subtotal: 'Sous-total',
      subtotalAfterDiscount: 'Sous-total Après Remise',
      vat: 'TVA',
      discount: 'Remise',
      grandTotal: 'Total Général',
      paidAmount: 'Montant Payé',
      dueAmount: 'Montant Dû',
      notes: 'Notes',
      phone: 'Téléphone',
      email: 'Email',
      status: 'Statut',
      paymentStatus: 'Statut de Paiement',
      thankYou: 'Merci pour votre confiance!',
    },
    nl: {
      saleOrder: 'Verkooporder',
      saleOrderNumber: 'Order Nr.',
      date: 'Datum',
      customer: 'Klantgegevens',
      sku: 'SKU/Barcode',
      description: 'Beschrijving',
      quantity: 'Aantal',
      unitPrice: 'Eenheidsprijs',
      total: 'Totaal',
      totalQuantity: 'Totaal Aantal',
      subtotal: 'Subtotaal',
      subtotalAfterDiscount: 'Subtotaal Na Korting',
      vat: 'BTW',
      discount: 'Korting',
      grandTotal: 'Totaal',
      paidAmount: 'Betaald Bedrag',
      dueAmount: 'Verschuldigd Bedrag',
      notes: 'Opmerkingen',
      phone: 'Telefoon',
      email: 'Email',
      status: 'Status',
      paymentStatus: 'Betalingsstatus',
      thankYou: 'Bedankt voor uw vertrouwen!',
    },
  };

  return translations[language] || translations.en;
}
